import { Module, MutationTree, ActionTree, GetterTree } from 'vuex';
import { RootState } from '@/store/types';
import { getFullFolderByVoucherCode } from '@/services/api/identification/api';
import { InfosDelegation } from '@/components/MesParametres/ParametresVaccination/ParametresDelegation/types';
import _ from 'lodash';
import {
	VaccinationFolderState,
	VaccinationFolder
} from './types';

const namespaced = true;

export const state: VaccinationFolderState = {
	vaccinationFolder: undefined,
	attestation: undefined,
	deletion: undefined,
	additionnalInjection: 0,
	lastCycle: false,
	error: false,
	cancellation: undefined,
	folderUpdateDisplay: false,
	folderUpdateAvailable: false,
	isRecapitulatif: false,
	reactivationAvailable: false
};

export const mutations: MutationTree<VaccinationFolderState> = {
	updateVaccinationFolder(state: VaccinationFolderState, vaccinationFolder: VaccinationFolder): void {
		state.vaccinationFolder = vaccinationFolder;
	},
	updateAttestationEnabled(state: VaccinationFolderState, attestation: boolean): void {
		state.attestation = attestation;
	},
	updateDeletionEnabled(state: VaccinationFolderState, deletion: boolean): void {
		state.deletion = deletion;
	},
	updateAdditionnalInjection(state: VaccinationFolderState, additionnalInjection: number): void {
		state.additionnalInjection = additionnalInjection;
	},
	updateIsLastCycle(state: VaccinationFolderState, lastCycle: boolean): void {
		state.lastCycle = lastCycle;
	},
	updateErrorVaccinationFolder(state: VaccinationFolderState, error: boolean): void {
		state.error = error;
	},
	cleanVaccinationFolderState(state: VaccinationFolderState) {
		state.vaccinationFolder = undefined;
		state.error = false;
	},
	updateCancellationEnabled(state: VaccinationFolderState, cancellation: boolean): void {
		state.cancellation = cancellation;
	},
	updateFolderUpdateDisplay(state: VaccinationFolderState, folderUpdateDisplay: boolean): void {
		state.folderUpdateDisplay = folderUpdateDisplay;
	},
	updateFolderUpdateAvailable(state: VaccinationFolderState, folderUpdateAvailable: boolean): void {
		state.folderUpdateAvailable = folderUpdateAvailable;
	},
	updateRecapitulatif(state: VaccinationFolderState, isRecapitulatif: boolean): void {
		state.isRecapitulatif = isRecapitulatif;
	},
	updateReactivationAvailable(state: VaccinationFolderState, reactivationAvailable: boolean): void {
		state.reactivationAvailable = reactivationAvailable;
	}
};

export const actions: ActionTree<VaccinationFolderState, RootState> = {
	fetchFolderByVoucherCode(state, data: ({ voucherCode: string, prescriptionId: number | undefined,
		psDelegation: InfosDelegation | undefined })) {
		return new Promise((resolve, reject) => {
			getFullFolderByVoucherCode(data.voucherCode, data.prescriptionId, data.psDelegation)
				.then((response: any) => {
					const vaccinationFolder: VaccinationFolder = new VaccinationFolder(response.data.value);
					state.commit('updateVaccinationFolder', vaccinationFolder);
					state.commit('updateAttestationEnabled', _.get(response, 'data.value.attestation'));
					state.commit('updateDeletionEnabled', _.get(response, 'data.value.deletion'));
					state.commit('updateAdditionnalInjection', _.get(response, 'data.value.additionnalInjection'));
					state.commit('updateIsLastCycle', _.get(response, 'data.value.lastCycle'));
					state.commit('updateCancellationEnabled', _.get(response, 'data.value.cancellation'));
					state.commit('updateFolderUpdateDisplay', _.get(response, 'data.value.folderUpdateDisplay'));
					state.commit('updateFolderUpdateAvailable', _.get(response, 'data.value.folderUpdateAvailable'));
					state.commit('updateReactivationAvailable', _.get(response, 'data.value.reactivationAvailable'));
					resolve(vaccinationFolder);
				})
				.catch((error: any) => {
					state.commit('updateErrorVaccinationFolder', true);
					reject(error);
				});
		});
	},
	updateVaccinationFolder(state, folder: VaccinationFolder): void {
		state.commit('updateVaccinationFolder', folder);
	},
	updateAttestationEnabled(state, attestation: boolean): void {
		state.commit('updateAttestationEnabled', attestation);
	},
	updateDeletionEnabled(state, deletion: boolean): void {
		state.commit('updateDeletionEnabled', deletion);
	},
	updateAdditionnalInjection(state, additionnalInjection: number): void {
		state.commit('additionnalInjection', additionnalInjection);
	},
	updateFolderAndFlags(state, response: any): void {
		state.commit('updateVaccinationFolder', _.get(response, 'data.value.dossier'));
		state.commit('updateAttestationEnabled', _.get(response, 'data.value.attestation') || false);
		state.commit('updateDeletionEnabled', _.get(response, 'data.value.deletion') || false);
		state.commit('updateAdditionnalInjection', _.get(response, 'data.value.additionnalInjection') || false);
		state.commit('updateIsLastCycle', _.get(response, 'data.value.lastCycle') || false);
		state.commit('updateCancellationEnabled', _.get(response, 'data.value.cancellation') || false);
		state.commit('updateFolderUpdateDisplay', _.get(response, 'data.value.folderUpdateDisplay') || false);
		state.commit('updateFolderUpdateAvailable', _.get(response, 'data.value.folderUpdateAvailable') || false);
		state.commit('updateReactivationAvailable', _.get(response, 'data.value.reactivationAvailable') || false);
	},
	cleanVaccinationFolder(state) {
		state.commit('cleanVaccinationFolderState');
	},
	updateCancellationEnabled(state, cancellation: boolean): void {
		state.commit('updateCancellationEnabled', cancellation);
	},
	updateFolderUpdateDisplay(state, folderUpdateDisplay: boolean): void {
		state.commit('updateFolderUpdateDisplay', folderUpdateDisplay);
	},
	updateFolderUpdateAvailable(state, folderUpdateAvailable: boolean): void {
		state.commit('updateFolderUpdateAvailable', folderUpdateAvailable);
	},
	updateRecapitulatif(state, isRecapitulatif: boolean): void {
		state.commit('updateRecapitulatif', isRecapitulatif);
	}
};

export const getters: GetterTree<VaccinationFolderState, RootState> = {
	getVaccinationFolder(state) {
		return state.vaccinationFolder;
	},
	getPrescription(state) {
		return _.get(state, 'vaccinationFolder.prescription');
	},
	getInjections(state) {
		return _.get(state, 'vaccinationFolder.prescription.injections');
	},
	getAttestationEnabled(state) {
		return state.attestation;
	},
	getDeletionEnabled(state) {
		return state.deletion;
	},
	getErrorVaccinationFolder(state) {
		return state.error;
	},
	getPrescripteur(state) {
		return _.get(state.vaccinationFolder, 'prescription.prescripteur');
	},
	getAdditionnalInjection(state) {
		return state.additionnalInjection;
	},
	getIsLastCycle(state) {
		return state.lastCycle;
	},
	getCancellationEnabled(state) {
		return state.cancellation;
	},
	getFolderUpdateDisplay(state) {
		return state.folderUpdateDisplay;
	},
	getFolderUpdateAvailable(state) {
		return state.folderUpdateAvailable;
	},
	isRecapitulatif(state) {
		return state.isRecapitulatif;
	},
	getReactivationAvailable(state) {
		return state.reactivationAvailable;
	}
};

export const infosVaccinationFolder: Module<VaccinationFolderState, RootState> = {
	namespaced,
	state,
	getters,
	actions,
	mutations
};
